import {
  Box,
  Card,
  CardContent,
  Divider,
  Stack,
  Typography
} from '@mui/material';
import Button from '@mui/material/Button';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useAtom } from 'jotai';
import { isEmpty, orderBy } from 'lodash';
import { useMemo } from 'react';

import CheckPermissions from '@/components/CheckPermissions';
import KeyValue, { type KeyValueModel } from '@/components/KeyValue';
import PERMISSIONS from '@/constants/auth.constants';
import { useStyles } from '@/features/DevicesTable';
import DEVICE_TYPES_CONFIG from '@/features/DevicesTable/DevicesTable.deviceTypes';
import {
  useClearDeviceStatus,
  useLogSourcesDialog
} from '@/features/LogSourcesCreate/LogSourcesCreate.hooks';
import { useCrudGuard } from '@/pages/Administration/Administration.hooks';

import { humanizeTime } from '../../Devices.service';
import { selectedDeviceDetailsAtom } from '../../Devices.store';
import DeviceTrendChart from '../../DeviceTrendChart/DeviceTrendChart';
import DeviceStatusCard from '../DeviceStatusCard';
import { LogSourcesCard } from '../LogSourcesCard';
import ViewAggregatedStatus from '../ViewAggregatedStatus/ViewAggregatedStatus';
import ViewSyncStatus from '../ViewSyncStatus/ViewSyncStatus';
import ViewToken from '../ViewToken/ViewToken';
import type { DeviceCardProps } from './DeviceCard.types';

type DeviceStatusItem = NonNullable<
  DeviceCardProps['items']['device_statuses']
>[number];

dayjs.extend(utc);

const SECTION_MARGIN_STYLE = { margin: '.875rem' } as const;
const SOURCE_TITLE_STYLE = {
  display: 'inline-flex',
  marginTop: '0.25rem'
} as const;
const SOURCE_EDIT_BUTTON_STYLE = {
  whiteSpace: 'nowrap',
  marginLeft: '0.875rem',
  float: 'inline-end'
} as const;
const CONFIG_CARD_STYLE = { maxHeight: '8rem', overflow: 'auto' } as const;

/**
 * Displays details and status sections for a selected device.
 *
 * @param props Component properties.
 * @returns Device details card content.
 */
const DeviceCard = (props: DeviceCardProps) => {
  const { classes } = useStyles();
  const { items, isMainDevicePage } = props;
  const [selectedDeviceDetail] = useAtom(selectedDeviceDetailsAtom);
  const hasSourceAccess =
    DEVICE_TYPES_CONFIG.find(
      (deviceTypeConfig) => deviceTypeConfig.id === items.device_type_id
    )?.hasSource || 0;
  const { openLogSourcesDialog } = useLogSourcesDialog();

  const { clearDeviceStatusMutation } = useClearDeviceStatus();

  const lastSeenDisplay = humanizeTime(items?.last_seen || '', true);
  const shouldShowSourceStatusSummary =
    Number(hasSourceAccess) === 1 &&
    Number(items?.device_type_id) === 1 &&
    Array.isArray(items?.lima_source_aggregated_status_display) &&
    items?.lima_source_aggregated_status_display.length > 0;

  // Check if device is receiving data (within last 2 hours)
  const isReceivingData = useMemo(() => {
    if (!items?.last_data_received_at) {
      return false;
    }

    const lastDataReceived = dayjs.utc(items.last_data_received_at).local();
    const twoHoursAgo = dayjs().subtract(2, 'hour');

    return lastDataReceived.isAfter(twoHoursAgo);
  }, [items?.last_data_received_at]);

  const shouldShowDeviceTrendChart =
    selectedDeviceDetail?.device_type?.id === 2 ||
    selectedDeviceDetail?.device_type?.id === 3;

  // Handling multiple Status Codes scenario
  // Sorting by status code in descending order to get the highest order of status code
  const deviceStatuses = orderBy(
    items?.device_statuses || [],
    'status_code',
    'desc'
  );

  const kvItems: KeyValueModel[] = [
    {
      label: 'Display Name',
      value: items?.display_name || ''
    },
    {
      label: 'Hostname',
      value: items.host_name
    },
    {
      label: 'Operating System',
      value: items.os
    },
    {
      label: 'Description',
      value: items.description
    },
    {
      label: 'Is Receiving Data',
      value: isReceivingData ? 'Yes' : 'No'
    }
  ];

  if (isMainDevicePage && items?.devices_tf_jobs?.last_updated) {
    const updatedAtDisplay = humanizeTime(
      items?.devices_tf_jobs?.last_updated || '',
      true
    );
    kvItems.push({
      label: 'Last Sync Updated',
      value: updatedAtDisplay
    });
  }

  const deviceHostInfoData: KeyValueModel[] = useMemo(() => {
    // The json object is retrieved from multiple external sources including but not limited to Aws SSM, Pritunl, etc.
    const hostInfo = (items?.host_info ?? {}) as Partial<DeviceHostInfo>;
    if (isEmpty(hostInfo)) {
      return [];
    }

    const deviceHostInfo = [
      {
        label: 'IPAddress',
        value: hostInfo?.aws_ssm?.ip_address || ''
      },
      {
        label: 'VPN IPAddress',
        value: hostInfo?.pritunl?.vpn_ipV4 || ''
      },
      // {
      //   label: 'MAC Address',
      //   value: hostInfo?.pritunl?.mac_addresses?.[0] || ''
      // },
      {
        label: 'Operating System',
        value:
          hostInfo?.aws_ssm?.platform_name &&
          hostInfo?.aws_ssm?.platform_version
            ? `${hostInfo.aws_ssm.platform_name} (${hostInfo.aws_ssm.platform_version})`
            : ''
      },
      {
        label: 'Last Connection',
        value: lastSeenDisplay
      }
    ];

    return deviceHostInfo;
  }, [items]);

  const editLogSourcesHandler = useCrudGuard(() => {
    openLogSourcesDialog('EditLogSources', items?.device_uid);
  });

  const hasErrorStatus = items?.device_statuses?.some(
    (status: DeviceStatusItem) =>
      status.status_code && status.status_code > 4000
  );
  const hasDeviceStatuses = deviceStatuses?.length > 0;

  const shouldShowLogSources =
    items?.device_type_id === 2 && !!items?.sources && items.sources.length > 0;

  const shouldShowSyncStatus =
    isMainDevicePage &&
    items?.device_type_id !== 1 &&
    (items?.devices_tf_jobs !== null || items?.devices_salt_jobs !== null);

  const handleClearStatus = () => {
    clearDeviceStatusMutation.mutate({
      host_name: items?.host_name,
      device_uid: items?.device_uid
    });
  };

  return (
    <CardContent style={{ overflow: 'auto' }} className={classes.cardContent}>
      {items && (
        <Box
          sx={{ margin: SECTION_MARGIN_STYLE.margin, pb: 6 }}
          key={items?.id || items?.host_name}
        >
          <Box id={`container-${items.id}`}>
            <KeyValue items={kvItems} />
            {!isEmpty(deviceHostInfoData) && (
              <>
                <Divider sx={{ my: 1 }} />
                <KeyValue items={deviceHostInfoData} />
              </>
            )}
            {items?.device_type_id !== 1 && <ViewToken items={items} />}
            {shouldShowSyncStatus && (
              <Box className={classes.KVFlex}>
                <ViewSyncStatus />
              </Box>
            )}
          </Box>
          {/* Device Trend Chart */}
          {shouldShowDeviceTrendChart && (
            <>
              <DeviceTrendChart />
              <Divider sx={{ my: 1 }} />
            </>
          )}
          {/* Device Multiple Status Codes rendering */}
          {hasDeviceStatuses && (
            <>
              <Stack
                spacing={1}
                direction="row"
                sx={{
                  mb: 1,
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <Typography variant="h6" sx={{ mt: 1, mb: 0.5 }}>
                  {items?.device_statuses && items.device_statuses.length > 1
                    ? 'STATUSES'
                    : 'STATUS'}
                </Typography>
                {hasErrorStatus && (
                  <CheckPermissions
                    permissions={[
                      PERMISSIONS.DEVICES_DELETE,
                      PERMISSIONS.INTERNAL
                    ]}
                  >
                    <Button
                      variant="outlined"
                      color="primary"
                      type="button"
                      onClick={handleClearStatus}
                    >
                      Clear Status
                    </Button>
                  </CheckPermissions>
                )}
              </Stack>

              {/* Show device statuses */}
              <Stack spacing={1}>
                {deviceStatuses?.map((status) => (
                  <DeviceStatusCard status={status} key={status.id} />
                ))}
              </Stack>
            </>
          )}
          {/* Show Log Sources */}
          {shouldShowLogSources && (
            <>
              <Typography variant="h6" sx={{ mt: 1.5, mb: 1.5 }}>
                <Box style={SOURCE_TITLE_STYLE}>SOURCES</Box>
                <CheckPermissions
                  permissions={[
                    PERMISSIONS.DEVICES_UPDATE,
                    PERMISSIONS.INTERNAL
                  ]}
                >
                  <Button
                    style={SOURCE_EDIT_BUTTON_STYLE}
                    variant="outlined"
                    color="primary"
                    type="button"
                    onClick={editLogSourcesHandler}
                  >
                    Edit
                  </Button>
                </CheckPermissions>
              </Typography>
              <LogSourcesCard data={items} />
            </>
          )}
          {/* Checking if the device type has source access from DEVICE_TYPES_CONFIG */}
          {shouldShowSourceStatusSummary && (
            <ViewAggregatedStatus
              items={items.lima_source_aggregated_status_display!}
              isMainDevicePage={isMainDevicePage}
            />
          )}

          {/* Show Configuration */}
          {items?.configuration && (
            <>
              <Typography variant="h6" sx={{ mt: 1.5, mb: 1.5 }}>
                <Box style={SOURCE_TITLE_STYLE}>CONFIGURATION</Box>
              </Typography>
              <Card variant="outlined" style={CONFIG_CARD_STYLE}>
                <CardContent>
                  <Stack spacing={0.5}>
                    <Typography
                      sx={{
                        mt: 1,
                        mb: 0.5,
                        whiteSpace: 'pre-wrap',
                        fontFamily: 'monospace'
                      }}
                    >
                      {items?.configuration
                        ? JSON.stringify(
                            JSON.parse(items.configuration),
                            null,
                            2
                          )
                        : 'No configuration'}
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>
            </>
          )}
        </Box>
      )}
    </CardContent>
  );
};

export default DeviceCard;

type DeviceHostInfo = {
  aws_ssm?: {
    ip_address?: string;
    platform_name?: string;
    platform_version?: string;
  };
  pritunl?: {
    vpn_ipV4?: string;
  };
};
